import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Table, Badge, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getRecipes, deleteRecipe, recalculateAllNutriScores } from '../../../services/RecipesService';
import { getMaterialEnglishName, getMaterialDutchName } from '../../../utils/materialName';
import RecipeFormModal from './RecipeFormModal';
import { NutriScoreBadge } from './NutriScoreSection';
import { ProductStatusBadge, ProductStatusChips, countByStatus, matchesStatusFilter } from '../Products/ProductStatus';

export default function Recipes() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewFilter, setViewFilter] = useState('all');
    const [recalcingAll, setRecalcingAll] = useState(false);

    const getRecipeDisplayName = (r) =>
        (i18n.language.startsWith('en') && r.name_en) ? r.name_en : (r.name_nl || r.name);

    const filteredRecipes = useMemo(() => {
        let list = recipes;
        list = list.filter(r => matchesStatusFilter(r, viewFilter));
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return list;
        return list.filter((r) => {
            const name = (r.name_nl || r.name || '').toLowerCase();
            const nameEn = (r.name_en || '').toLowerCase();
            const materials = (r.items || [])
                .map((it) => `${getMaterialEnglishName(it)} ${getMaterialDutchName(it)}`.toLowerCase())
                .join(' ');
            return name.includes(q) || nameEn.includes(q) || materials.includes(q);
        });
    }, [recipes, searchQuery, viewFilter, i18n.resolvedLanguage]);

    const statusCounts = useMemo(() => countByStatus(recipes), [recipes]);

    const statusTooltip = (r) => {
        const prods = r.linked_products || [];
        if (!prods.length) return t('recipes.notLinked');
        const list = prods
            .map((p) => `${(i18n.language.startsWith('en') && p.name_en) ? p.name_en : p.name} (${p.sales_status ? t(`products.status.${p.sales_status}`) : '—'})`)
            .join(', ');
        return t('recipes.usedBy', { list });
    };

    const loadRecipes = async () => {
        try {
            setLoading(true);
            const res = await getRecipes();
            setRecipes(res.data || []);
        } catch {
            toast.error(t('recipes.errors.load'));
            setRecipes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRecipes();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        setShowModal(true);
    };

    const handleEdit = (id) => {
        setEditingId(id);
        setShowModal(true);
    };

    const handleRecalcAll = async () => {
        setRecalcingAll(true);
        try {
            const res = await recalculateAllNutriScores();
            const { updated } = res.data;
            toast.success(t('nutriscore.all_calculated', { count: updated }));
            loadRecipes();
        } catch {
            toast.error(t('nutriscore.errors.calculate'));
        } finally {
            setRecalcingAll(false);
        }
    };

    const handleDelete = async (recipe) => {
        const result = await Swal.fire({
            title: t('recipes.deleteTitle'),
            text: t('recipes.deleteText', { name: recipe.name_nl || recipe.name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `${t('common.yes')}, ${t('common.delete').toLowerCase()}`,
        });
        if (result.isConfirmed) {
            try {
                await deleteRecipe(recipe.id);
                toast.success(t('recipes.success.deleted'));
                loadRecipes();
            } catch {
                toast.error(t('recipes.errors.delete'));
            }
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 className="heading mb-0">{t('recipes.title')}</h4>
                                <span className="text-muted">{t('recipes.subtitle')}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="product-filter-row">
                                <ProductStatusChips value={viewFilter} onChange={setViewFilter} counts={statusCounts} />
                                <div className="input-group products-search-bar">
                                    <span className="input-group-text bg-light">
                                        <i className="fa-solid fa-magnifying-glass text-muted" />
                                    </span>
                                    <Form.Control
                                        type="search"
                                        placeholder={t('recipes.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        aria-label={t('recipes.searchAria')}
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setSearchQuery('')}
                                            title={t('common.clearSearch')}
                                            aria-label={t('common.clearSearch')}
                                        >
                                            <i className="fa-solid fa-xmark text-muted" />
                                        </button>
                                    )}
                                </div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="text-nowrap"
                                    onClick={handleRecalcAll}
                                    disabled={recalcingAll}
                                    title={t('nutriscore.calculate_all')}
                                >
                                    <i className="fa-solid fa-calculator me-1" />
                                    {recalcingAll ? t('nutriscore.calculating_all') : t('nutriscore.calculate_all')}
                                </Button>
                                <Button variant="primary" onClick={handleAdd} className="text-nowrap mobile-toolbar-add-btn">
                                    <i className="fa-solid fa-plus me-1" /> {t('recipes.addRecipe')}
                                </Button>
                            </div>
                            {searchQuery && recipes.length > 0 && (
                                <div className="mb-2">
                                    <small className="text-muted">
                                        {t('common.showingResults', { shown: filteredRecipes.length, total: recipes.length, label: 'recipes' })}
                                        <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setSearchQuery('')}>
                                            {t('common.clearSearch')}
                                        </button>
                                    </small>
                                </div>
                            )}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" />
                                    <p className="mt-2 text-muted mb-0">{t('recipes.loading')}</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle recipes-main-table">
                                        <thead>
                                            <tr>
                                                <th>{t('recipes.columns.recipe')}</th>
                                                <th className="text-center">{t('recipes.columns.makes')}</th>
                                                <th className="text-center">{t('recipes.columns.ingredients')}</th>
                                                <th className="text-center">{t('nutriscore.title')}</th>
                                                <th className="text-center">{t('recipes.columns.per1Excl')}</th>
                                                <th className="text-center">{t('recipes.columns.per1Incl')}</th>
                                                <th className="text-end">{t('recipes.columns.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recipes.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-5 text-muted">
                                                        <p className="mb-1">{t('recipes.noRecipesYet')}</p>
                                                        <small>{t('recipes.createFirstRecipe')}</small>
                                                    </td>
                                                </tr>
                                            ) : filteredRecipes.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-5 text-muted">
                                                        <p className="mb-1">{t('recipes.noRecipesMatch', { query: searchQuery })}</p>
                                                        <small>{t('recipes.tryDifferentKeywords')}</small>
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredRecipes.map((r) => (
                                                    <tr key={r.id}>
                                                        <td>
                                                            <strong>{getRecipeDisplayName(r)}</strong>
                                                            <ProductStatusBadge status={r.sales_status} title={statusTooltip(r)} />
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="light" text="dark">
                                                                {r.yield_amount} {(r.yield_unit && !['1', 'unit'].includes(String(r.yield_unit).toLowerCase())) ? r.yield_unit : 'pieces'}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">{t('recipes.itemsCount', { count: r.items?.length || 0 })}</td>
                                                        <td className="text-center">
                                                            {r.nutriscore_letter
                                                                ? <NutriScoreBadge letter={r.nutriscore_letter} compact />
                                                                : <span style={{ color: '#ccc', fontSize: 16 }}>—</span>}
                                                        </td>
                                                        <td className="text-center">
                                                            {(r.cost_per_unit_excl ?? (r.items?.reduce((s, it) => s + (parseFloat(it.cost_excl_tax) || 0), 0) / (r.yield_amount || 1)))?.toFixed(2) ?? '-'}
                                                        </td>
                                                        <td className="text-center">
                                                            {(r.cost_per_unit ?? (r.total_cost / (r.yield_amount || 1)))?.toFixed(2) ?? '-'}
                                                        </td>
                                                        <td className="text-end">
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => navigate(`/recipes/${r.id}`)}
                                                                title={t('common.view')}
                                                            >
                                                                <i className="fa-solid fa-eye text-info" />
                                                            </Button>
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => handleEdit(r.id)}
                                                                title={t('common.edit')}
                                                            >
                                                                <i className="fa-solid fa-pen text-primary" />
                                                            </Button>
                                                            <Button
                                                                variant="light"
                                                                size="sm"
                                                                className="text-danger"
                                                                onClick={() => handleDelete(r)}
                                                                title={t('common.delete')}
                                                            >
                                                                <i className="fa-solid fa-trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <RecipeFormModal
                show={showModal}
                onHide={() => setShowModal(false)}
                recipeId={editingId}
                onSaved={loadRecipes}
            />
        </div>
    );
}
